import { config } from '../../../../config';
import LinkButton from '../../../components/LinkButton';
import NotFound from '../../../components/NotFound';
import PageMeta from '../../../components/PageMeta';
import QuestionTypeIcon from '../../../components/Votings/QuestionTypeIcon';
import {
  VotingBadge,
  VotingBadges,
} from '../../../components/Votings/VotingBadges';
import VotingTicket from '../../../components/Votings/VotingTicket';
import { getVotingDetail } from '../../../features/queries/getVotingDetail';
import { entityId } from '../../../lib/ids';
import { useQ } from '../../../lib/query';
import {
  answersFromMyVotes,
  citizenResultsVisible,
  citizenVotingStatus,
  questionSummary,
  votingResultsPath,
  votingResultsPublished,
  votingRunPath,
  votingState,
} from '../../../lib/votings';
import { useLayout } from '../../../providers/LayoutProvider';
import {
  __,
  declension,
  formatDateTime,
  getVotesDeclension,
} from '../../../utils';
import CheckIcon from '@mui/icons-material/Check';
import LoginIcon from '@mui/icons-material/Login';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import { useAuth } from '@presov.sk/presov-auth-react';
import {
  Box,
  Button,
  Card,
  Divider,
  MarkdownPreview,
  Skeleton,
  Stack,
  Typography,
} from '@presov.sk/presov-ui-react';
import React from 'react';
import { useParams } from 'react-router';

/**
 * Úvod hlasovania - to, čo občan vidí **pred** vypĺňaním.
 *
 * Je to obrazovka, na ktorej sa rozhodne prihlásenie a overenie účtu. Predtým
 * to boli `Alert`-y nad otázkami: človek si prečítal zadanie, začal vypĺňať
 * a až tam sa dozvedel, že hlasovať nesmie. Odteraz je podmienka podmienkou -
 * stojí pred formulárom a jej akcia je akciou stránky.
 *
 * Samotné hlasovanie je vlastná adresa ([[VotingRunPage]]), výsledky tiež
 * ([[VotingResultsPage]]). Tabov tu preto niet: každý z tých troch pohľadov má
 * inú pätičku a tab, ktorý mení pätičku, sa číta ako iná stránka.
 */
/**
 * Riadok panela faktov - label vľavo, hodnota vpravo. Hodnota sa smie zalomiť
 * (dátum s časom je dlhý), label sa zmrští; rovnaká dohoda ako v administrácii.
 */
const InfoRow: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <Stack direction='row' alignItems='baseline' gap={1.25}>
    <Typography
      variant='body2'
      color='text.secondary'
      sx={{ flex: '1 1 auto', minWidth: 0 }}
    >
      {label}
    </Typography>
    <Typography
      variant='body2'
      sx={{
        flex: '0 1 auto',
        minWidth: 0,
        fontWeight: 500,
        textAlign: 'right',
        lineHeight: 1.4,
      }}
    >
      {value}
    </Typography>
  </Stack>
);

const VotingDetailPage: React.FC = () => {
  const { votingId: votingIdParam } = useParams() as { votingId: string };
  const votingId = entityId(votingIdParam, 'voting');
  const { setPageTitle } = useLayout();
  const { authUser, generateLoginUrl, authState } = useAuth();

  const now = React.useMemo(() => new Date(), []);

  const {
    data: detail,
    isLoading,
    isError,
    error,
  } = useQ(
    getVotingDetail,
    { votingId },
    {
      // Vždy načítať znova - občan sa sem vracia hneď po odoslaní hlasu.
      refetchOnMount: 'always',
      // Kým `AuthProvider` nedobehne, token na klientovi nie je a neverejné
      // hlasovanie by server odmietol ako neprihlásenému - stránka by ukázala
      // „Toto hlasovanie nie je verejné". Detail v [[VotingsPage]].
      enabled: authState !== 'authorizing',
    },
  );

  const state = detail ? votingState(detail, now) : undefined;
  const status = detail ? citizenVotingStatus(detail, now) : undefined;

  const myAnswers = React.useMemo(
    () => answersFromMyVotes(detail?.my_votes),
    [detail?.my_votes],
  );

  const needsVerification =
    !!detail?.verified_accounts_only && !authUser?.verified;
  const needsLogin = authState === 'unauthorized' && !detail?.allow_anonymous;

  React.useEffect(() => {
    setPageTitle(detail?.title ?? __('Hlasovanie'));
  }, [detail?.title, setPageTitle]);

  if (isError && authState !== 'authorizing') {
    return (
      <NotFound
        message={
          error?.details?.statusCode === 403
            ? __('Toto hlasovanie nie je verejné.')
            : __('Hlasovanie sa nenašlo.')
        }
        backUrl='/hlasovania'
        backText={__('Späť na hlasovania')}
      />
    );
  }

  if (isLoading || authState === 'authorizing' || !detail) {
    return (
      <Box
        sx={{
          width: '100%',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 340px' },
          gap: 2,
          alignItems: 'start',
        }}
      >
        <Stack gap={1.5}>
          <Skeleton variant='rounded' height={160} />
          <Skeleton variant='rounded' height={200} />
        </Stack>
        <Skeleton variant='rounded' height={200} />
      </Box>
    );
  }

  // Akcie hlasovania - v karte faktov, nie v pätičke shellu. Primárna akcia
  // závisí od stavu a od toho, čo účtu chýba (prihlásenie/overenie).
  // Hlasovanie, ktoré výsledky nezverejňuje, odkaz nedostane vôbec - ani po
  // uzavretí, ani pre toho, kto už hlasoval. Kým beží a priebežné výsledky
  // zapnuté nemá, odkaz pre odhlasovaného zostáva: stránka výsledkov mu
  // povie počet hlasov a kedy podrobnosti prídu.
  const resultsVisible =
    citizenResultsVisible(detail, now) ||
    (votingResultsPublished(detail) && !!detail.already_voted);

  const primaryAction: React.ReactNode =
    detail.already_voted ||
    state === 'draft' ||
    state === 'closed' ? null : needsLogin ? (
      <Button
        fullWidth
        startIcon={<LoginIcon fontSize='small' />}
        onClick={() => {
          window.location.href = generateLoginUrl();
        }}
      >
        {__('Prihlásiť sa a hlasovať')}
      </Button>
    ) : needsVerification ? (
      <Button
        fullWidth
        endIcon={<OpenInNewIcon fontSize='small' />}
        component='a'
        {...({
          href: config.AUTH_SERVER,
          target: '_blank',
          rel: 'noreferrer',
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any)}
      >
        {__('Overiť účet')}
      </Button>
    ) : (
      <LinkButton fullWidth to={votingRunPath(votingId)}>
        {__('Hlasovať')}
      </LinkButton>
    );

  const votingActions =
    primaryAction || resultsVisible ? (
      <>
        <Divider />
        <Stack gap={1}>
          {primaryAction}
          {resultsVisible && (
            <LinkButton
              variant='outline'
              fullWidth
              to={votingResultsPath(votingId)}
            >
              {__('Zobraziť výsledky')}
            </LinkButton>
          )}
        </Stack>
      </>
    ) : null;

  return (
    <>
      {/* Markdown v meta tagu nemá čo vykresliť - `PageMeta` z popisu
          nechá text, to isté odstrojenie ako v zozname ([[votings]]). */}
      {detail ? (
        <PageMeta
          title={`${__('Hlasovanie')}: ${detail.title}`}
          description={detail.description}
          type='article'
        />
      ) : null}

      <Stack gap={2} sx={{ width: '100%' }}>
        {/* Dva stĺpce na desktope: vľavo zadanie a otázky, vpravo panel faktov
            (stav, dokedy, hlasy, otázky, kto smie hlasovať). Predtým to bol
            jeden úzky stĺpec 760 px a fakty len ako pilulky nad ním - teraz je
            viac vecí naraz a na širšej ploche. Na mobile ide panel navrch
            (order -1), aby fakty ostali hore ako predtým. */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 340px' },
            gap: 2,
            alignItems: 'start',
          }}
        >
          <Stack gap={2} sx={{ minWidth: 0 }}>
            {/* Podmienka účasti stojí pred zadaním, nie nad otázkami. */}
            {!detail.already_voted && state === 'open' && needsVerification && (
              <Card
                sx={{
                  flexDirection: 'row',
                  alignItems: 'flex-start',
                  gap: 1.75,
                  p: 2.5,
                }}
              >
                <Box
                  sx={({ palette, shape }) => ({
                    width: 36,
                    height: 36,
                    flex: 'none',
                    display: 'grid',
                    placeItems: 'center',
                    borderRadius: `${shape.borderRadiusFull}px`,
                    backgroundColor: palette.yellow.surface,
                    border: `1px solid ${palette.yellow.surfaceBorder}`,
                    color: palette.yellow.surfaceText,
                  })}
                >
                  <VerifiedUserOutlinedIcon sx={{ fontSize: 18 }} />
                </Box>
                <Stack gap={0.75}>
                  <Typography
                    component='b'
                    sx={{ fontSize: 17, fontWeight: 700 }}
                  >
                    {__('V tomto hlasovaní hlasujú iba overené účty')}
                  </Typography>
                  <Typography variant='body1' color='text.secondary'>
                    {__(
                      'Váš účet zatiaľ overený nie je. Overenie je jednorazové a robí sa cez eID alebo na klientskom centre; potom sa dá hlasovať hneď. Zadanie a otázky si môžete prečítať aj bez overenia - odoslať hlas nie.',
                    )}
                  </Typography>
                </Stack>
              </Card>
            )}

            {!detail.already_voted && state === 'open' && needsLogin && (
              <Card sx={{ p: 2.5, gap: 0.75 }}>
                <Typography
                  component='b'
                  sx={{ fontSize: 17, fontWeight: 700 }}
                >
                  {__('Hlasovať môžu iba prihlásení používatelia')}
                </Typography>
                <Typography variant='body1' color='text.secondary'>
                  {__(
                    'Prihlásením sa hlasovanie zapíše k vášmu účtu, takže si ho z omylu nezopakujete.',
                  )}
                </Typography>
              </Card>
            )}

            {detail.already_voted && (
              <Stack
                direction='row'
                alignItems='center'
                gap={1.25}
                sx={({ palette, shape }) => ({
                  border: `1px solid ${palette.divider}`,
                  borderRadius: `${shape.borderRadius}px`,
                  backgroundColor: palette.grey[50],
                  px: 1.75,
                  py: 1.5,
                })}
              >
                <Box
                  sx={({ palette, shape }) => ({
                    width: 22,
                    height: 22,
                    flex: 'none',
                    display: 'grid',
                    placeItems: 'center',
                    borderRadius: `${shape.borderRadiusFull}px`,
                    backgroundColor: palette.green.main,
                    color: palette.common.white,
                  })}
                >
                  <CheckIcon sx={{ fontSize: 14 }} />
                </Box>
                <Typography sx={{ fontSize: 14, lineHeight: 1.45 }}>
                  {myAnswers
                    ? __(
                        'V tomto hlasovaní ste už hlasovali. Nižšie sú vaše odpovede - meniť sa už nedajú.',
                      )
                    : /* Lístok je spred zavedenia kľúčov, alebo bol anonymný -
                     nemáme čo zobraziť a nemáme to ani predstierať. */
                      __(
                        'V tomto hlasovaní ste už hlasovali. Vaše odpovede sa nepodarilo načítať, takže ich nižšie neuvidíte.',
                      )}
                </Typography>
              </Stack>
            )}

            {detail.description && (
              <Card sx={{ p: 3 }}>
                <MarkdownPreview value={detail.description} />
              </Card>
            )}

            {detail.already_voted && myAnswers ? (
              <VotingTicket questions={detail.questions} answers={myAnswers} />
            ) : (
              <Card sx={{ p: 3, gap: 1.75 }}>
                <Typography
                  variant='overline'
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: 'text.secondary',
                  }}
                >
                  {__('Otázky v hlasovaní')}
                </Typography>
                <Stack gap={1.5}>
                  {detail.questions.map((question, index) => (
                    <Stack
                      key={question.question_id}
                      direction='row'
                      gap={1.5}
                      alignItems='flex-start'
                    >
                      <Box
                        sx={({ palette, shape }) => ({
                          width: 26,
                          height: 26,
                          flex: 'none',
                          display: 'grid',
                          placeItems: 'center',
                          borderRadius: `${shape.borderRadiusSm + 2}px`,
                          backgroundColor: palette.grey[100],
                          color: palette.grey[600],
                        })}
                      >
                        <QuestionTypeIcon type={question.type} size={14} />
                      </Box>
                      <Stack gap={0.25}>
                        <Typography
                          component='b'
                          sx={{
                            fontSize: 15,
                            fontWeight: 700,
                            lineHeight: 1.4,
                          }}
                        >
                          {`${index + 1} · ${question.title}`}
                        </Typography>
                        <Typography variant='body2' color='text.secondary'>
                          {questionSummary(question)}
                        </Typography>
                      </Stack>
                    </Stack>
                  ))}
                </Stack>
              </Card>
            )}
          </Stack>

          {/* Pravý panel faktov - to isté, čo bolo v pilulkách, ale
                pohromade a čitateľne; na mobile navrchu. */}
          <Card sx={{ p: 2.5, gap: 1.75, order: { xs: -1, md: 0 } }}>
            <VotingBadges>
              {status && (
                <VotingBadge
                  dot
                  tone={
                    state === 'open'
                      ? 'info'
                      : state === 'draft'
                        ? 'warning'
                        : 'neutral'
                  }
                >
                  {status.label}
                </VotingBadge>
              )}
              {detail.verified_accounts_only && (
                <VotingBadge tone='warning'>
                  {__('Iba overené účty')}
                </VotingBadge>
              )}
            </VotingBadges>

            <Divider />

            <Stack gap={1.25}>
              {state === 'draft' && detail.open_from && (
                <InfoRow
                  label={__('Otvára sa')}
                  value={formatDateTime(detail.open_from)}
                />
              )}
              {state !== 'draft' && detail.open_to && (
                <InfoRow
                  label={
                    state === 'closed' ? __('Skončilo') : __('Otvorené do')
                  }
                  value={formatDateTime(detail.open_to)}
                />
              )}
              <InfoRow
                label={__('Počet hlasov')}
                value={`${detail.votes_count} ${getVotesDeclension(detail.votes_count)}`}
              />
              <InfoRow
                label={__('Otázok')}
                value={`${detail.questions.length} ${declension(
                  detail.questions.length,
                  [__('otázka'), __('otázky'), __('otázok')],
                )}`}
              />
              <InfoRow
                label={__('Hlasovať môžu')}
                value={
                  detail.allow_anonymous
                    ? __('Ktokoľvek')
                    : detail.verified_accounts_only
                      ? __('Overené účty')
                      : __('Prihlásení')
                }
              />
            </Stack>

            {votingActions}
          </Card>
        </Box>
      </Stack>
    </>
  );
};

export default VotingDetailPage;
